import React, { useRef, useState } from 'react';
import type { FC } from 'react';
import { styled } from '@compiled/react';

import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import { xcss } from '@atlaskit/primitives/xcss';
import Badge from '@atlaskit/badge/badge';
import DropdownMenu from '@atlaskit/dropdown-menu/dropdown-menu';
import DropdownItem from '@atlaskit/dropdown-menu/dropdown-menu-item';
import DropdownItemGroup from '@atlaskit/dropdown-menu/dropdown-menu-item-group';
import type { OnOpenChangeArgs } from '@atlaskit/dropdown-menu/types';

import {
	UNSAFE_expValNoExposure,
	UNSAFE_expValEqualsNoExposure,
} from '@confluence/feature-experiments';
import { ForgeKeyboardShortcutVisualizer } from '@confluence/forge-ui/entry-points/ForgeKeyboardShortcut.ts';

export type ExpandableNestedMenuItemItemType = {
	id: string;
	label: string;
	tooltip?: string;
	url: string;
	badgeLabel?: string;
	styleClass?: string; // Some Connect apps have a styleClass to apply custom styles
	customComponent?: () => React.ReactElement;
	onClick?: () => void;
	closeParentMenu?: () => void; // Need this prop when we don't use a custom component
};

export type ExpandableNestedMenuItemProps = {
	label: string | React.ReactNode;
	items: ExpandableNestedMenuItemItemType[]; // Connect menu items
	isNestedMenuOpen: boolean;
	isWhiteboardTemplateMenu?: boolean;
	onClick: () => void;
	iconBefore?: React.ReactNode;
	forgeMenuItems?: React.ReactElement[]; // Forge menu items
	onMenuClose: () => void;
	id?: string;
};

// this is to keep consistent with ContentTools PopupMenuGroup
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledDropdownItemGroup = styled(DropdownItemGroup)({
	minWidth: '220px',
	maxWidth: '800px',
});

// styled(DropdownItem) doesn't apply css to component
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DropdownItemLabelContainer = styled.span({
	display: 'flex',
	justifyContent: 'space-between',
});

const badgeContainerStyles = xcss({
	paddingLeft: 'space.075',
});

// Don't want to apply aui button styles in the dropdown menu since style needs to be uniform
export const getStyleClass = (styleClass?: string): string | undefined => {
	return !styleClass?.includes('aui-button') ? styleClass : '';
};

export const ExpandableNestedMenuItem: FC<ExpandableNestedMenuItemProps> = ({
	label,
	items,
	isNestedMenuOpen,
	isWhiteboardTemplateMenu = false,
	onClick,
	iconBefore,
	forgeMenuItems,
	onMenuClose,
	id,
}) => {
	const [shouldReturnFocus, setShouldReturnFocus] = useState(true);
	const skipFocusReturnRef = useRef<HTMLElement>(null);
	if (!items.length && !forgeMenuItems?.length) return null;
	const menuItems = (
		<StyledDropdownItemGroup>
			{items.map((item) => {
				if (item.customComponent) {
					return item.customComponent();
				}
				const {
					id,
					url,
					label: itemLabel,
					badgeLabel,
					tooltip,
					onClick: onItemClick,
					closeParentMenu,
				} = item;
				return (
					<div
						// className is needed here for Connect modals to show properly
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
						className={getStyleClass(item?.styleClass)}
						key={id}
					>
						<DropdownItem
							href={url}
							onClick={() => {
								onItemClick?.();
								closeParentMenu?.();
							}}
							UNSAFE_shouldDisableRouterLink
						>
							<DropdownItemLabelContainer>
								{/* Some Connect apps don't have a label so we'll use the tooltip as the text since it's the next best thing */}
								<span>{itemLabel || tooltip}</span>
								{badgeLabel && (
									<Box xcss={badgeContainerStyles}>
										<Badge>{badgeLabel}</Badge>
									</Box>
								)}
							</DropdownItemLabelContainer>
						</DropdownItem>
					</div>
				);
			})}
			{forgeMenuItems &&
				forgeMenuItems.length > 0 &&
				forgeMenuItems.map((forgeMenuItem) => (
					<ForgeKeyboardShortcutVisualizer
						key={forgeMenuItem?.props?.extension?.id}
						module={forgeMenuItem?.props?.extension}
					>
						{forgeMenuItem}
					</ForgeKeyboardShortcutVisualizer>
				))}
		</StyledDropdownItemGroup>
	);
	return (
		// TODO: Add `shouldRenderToParent` to component to improve accessibility. See
		// https://a11y.atlassian.net/browse/JCATS-788
		// https://hello.atlassian.net/wiki/spaces/DST/pages/5402216605/shouldRenderToParent+prop+usages+in+cc-disco+packages
		<DropdownMenu
			returnFocusRef={
				!shouldReturnFocus &&
				isWhiteboardTemplateMenu &&
				UNSAFE_expValEqualsNoExposure('cc-whiteboards-lovability', 'isEnabled', true) &&
				UNSAFE_expValNoExposure('cc-whiteboards-lovability', 'releaseLevel', 0) >= 1
					? skipFocusReturnRef
					: undefined
			}
			placement="left-start"
			isOpen={isNestedMenuOpen}
			// need this as onClose/onBlur handler for proper focus management
			onOpenChange={({ isOpen }: OnOpenChangeArgs) => {
				if (!isOpen) {
					onMenuClose();
				}
			}}
			trigger={({ triggerRef, ...triggerProps }) => (
				<DropdownItem
					{...triggerProps}
					ref={triggerRef}
					onClick={() => {
						if (
							isWhiteboardTemplateMenu &&
							UNSAFE_expValEqualsNoExposure('cc-whiteboards-lovability', 'isEnabled', true) &&
							UNSAFE_expValNoExposure('cc-whiteboards-lovability', 'releaseLevel', 0) >= 1
						) {
							setShouldReturnFocus(true);
						}
						onClick();
					}}
					elemAfter={<ChevronRightIcon label="" size="small" />}
					elemBefore={iconBefore}
					testId={`${id}-button`}
				>
					{label}
				</DropdownItem>
			)}
		>
			{isWhiteboardTemplateMenu &&
			UNSAFE_expValEqualsNoExposure('cc-whiteboards-lovability', 'isEnabled', true) &&
			UNSAFE_expValNoExposure('cc-whiteboards-lovability', 'releaseLevel', 0) >= 1 ? (
				<div
					onMouseDownCapture={() => setShouldReturnFocus(false)}
					onKeyDownCapture={() => setShouldReturnFocus(true)}
				>
					{menuItems}
				</div>
			) : (
				menuItems
			)}
		</DropdownMenu>
	);
};
